import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Coins, LineChart as LineIcon } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function PrizePayoutHistory() {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [state, setState] = useState('loading');

  useEffect(() => {
    api.get('/seasons/payouts?limit=200').then(r => {
      setPayouts(r.data.payouts || []);
      setTotal(r.data.total_paid_usdt || 0);
      setCount(r.data.count || 0);
      setState('ready');
    }).catch(() => setState('empty'));
  }, []);

  // Build cumulative time-series data — sort ASC by awarded_at, accumulate prize
  const series = useMemo(() => {
    if (!payouts.length) return [];
    const sorted = [...payouts].sort((a, b) => new Date(a.awarded_at) - new Date(b.awarded_at));
    let running = 0;
    return sorted.map(p => {
      running += (p.prize_usdt || 0);
      return {
        t: new Date(p.awarded_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        total: Math.round(running),
        prize: p.prize_usdt,
        label: `S${p.season_id + 1} · ${p.medal} ${p.user_name}`,
      };
    });
  }, [payouts]);

  if (state === 'loading') return null;
  if (!payouts.length) return null;

  return (
    <div className="mt-6" data-testid="prize-payout-history">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-[#0ECB81]" />
          <h3 className="text-white text-lg font-semibold">Prize Payout History</h3>
          <span className="text-xs uppercase tracking-wider text-[#848E9C]">transparent · public ledger</span>
        </div>
        <div className="text-right">
          <div className="text-xs text-[#848E9C]">Total paid</div>
          <div className="font-mono text-[#0ECB81] text-lg" data-testid="total-paid">
            {total.toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-xs text-[#848E9C]">USDT · {count}</span>
          </div>
        </div>
      </div>

      {/* Running cumulative chart */}
      <div className="border border-[#2B3139] bg-[#181A20] p-3 mb-3" data-testid="payout-chart">
        <div className="flex items-center gap-2 mb-2">
          <LineIcon className="w-3.5 h-3.5 text-[#0ECB81]" />
          <span className="text-xs uppercase tracking-wider text-[#848E9C]">Cumulative payouts over time</span>
        </div>
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <defs>
                <linearGradient id="gPayout" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ECB81" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#0ECB81" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#2B3139" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="t" stroke="#848E9C" tick={{ fontSize: 10, fill: '#848E9C' }} tickLine={false} axisLine={{ stroke: '#2B3139' }} />
              <YAxis stroke="#848E9C" tick={{ fontSize: 10, fill: '#848E9C' }} tickLine={false} axisLine={{ stroke: '#2B3139' }}
                tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}K` : String(v)} width={48} />
              <Tooltip
                contentStyle={{ background: '#0B0E11', border: '1px solid #2B3139', fontSize: 12 }}
                labelStyle={{ color: '#B7BDC6' }}
                formatter={(v, _n, entry) => [`${Math.round(v).toLocaleString()} USDT`, entry?.payload?.label || 'total']}
              />
              <Line type="monotone" dataKey="total" stroke="#0ECB81" strokeWidth={2}
                dot={{ r: 3, fill: '#0ECB81', stroke: '#0ECB81' }} activeDot={{ r: 5 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-[#2B3139] bg-[#181A20] max-h-72 overflow-y-auto">
        <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139] sticky top-0 bg-[#181A20]">
          <div className="col-span-2">Season</div>
          <div className="col-span-1">Place</div>
          <div className="col-span-5">Winner</div>
          <div className="col-span-2 text-right">Streak</div>
          <div className="col-span-2 text-right">Payout</div>
        </div>
        {payouts.map(p => (
          <div key={p.award_id} className="grid grid-cols-12 px-4 py-2 items-center border-b border-[#2B3139]/60 text-sm hover:bg-[#0B0E11]" data-testid={`payout-row-${p.award_id}`}>
            <div className="col-span-2 font-mono text-[#B7BDC6] text-xs">S{p.season_id + 1}</div>
            <div className="col-span-1 text-lg leading-none">{p.medal}</div>
            <div className="col-span-5 flex items-center gap-2 min-w-0">
              {p.user_picture ? (
                <img src={p.user_picture} alt="" className="w-5 h-5 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-[#2B3139] grid place-items-center text-[#EAECEF] text-[9px] font-semibold flex-shrink-0">
                  {p.user_name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <a href={`/trader/${p.user_id}`} className="text-white truncate hover:text-[#FCD535]">{p.user_name}</a>
            </div>
            <div className="col-span-2 text-right font-mono text-[#F6465D] text-xs">{p.streak_at_award}🔥</div>
            <div className="col-span-2 text-right font-mono text-[#0ECB81] text-sm">+{p.prize_usdt?.toFixed(0)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
